/**
 * Стек undo/redo правок фич тактической доски (command inverse + remap id).
 * Паттерн как у VisualizerSignHistory.
 */
import { featureToCreatePayload as boardFeatureToCreatePayload } from "@/tactical/io/boardPackage";
import type { TacticalFeature } from "@/services/tactical.service";

export const TACTICAL_FEATURE_HISTORY_MAX = 50;

export type TacticalHistoryEntry = {
  undo: () => Promise<void>;
  redo: () => Promise<void>;
};

export class TacticalFeatureHistory {
  private entries: TacticalHistoryEntry[] = [];
  private index = -1;
  private readonly idMap = new Map<number, number>();

  constructor(private readonly maxDepth: number = TACTICAL_FEATURE_HISTORY_MAX) {}

  clear(): void {
    this.entries = [];
    this.index = -1;
    this.idMap.clear();
  }

  get canUndo(): boolean {
    return this.index >= 0;
  }

  get canRedo(): boolean {
    return this.index < this.entries.length - 1;
  }

  push(entry: TacticalHistoryEntry): void {
    this.entries = this.entries.slice(0, this.index + 1);
    this.entries.push(entry);
    while (this.entries.length > this.maxDepth) {
      this.entries.shift();
    }
    this.index = this.entries.length - 1;
  }

  async undo(): Promise<boolean> {
    if (!this.canUndo) return false;
    const entry = this.entries[this.index];
    this.index -= 1;
    await entry.undo();
    return true;
  }

  async redo(): Promise<boolean> {
    if (!this.canRedo) return false;
    this.index += 1;
    const entry = this.entries[this.index];
    await entry.redo();
    return true;
  }

  resolveId(id: number): number {
    let cur = id;
    const seen = new Set<number>();
    while (this.idMap.has(cur) && !seen.has(cur)) {
      seen.add(cur);
      cur = this.idMap.get(cur)!;
    }
    return cur;
  }

  remapId(from: number, to: number): void {
    if (from === to) return;
    this.idMap.set(from, to);
    for (const [key, value] of [...this.idMap.entries()]) {
      if (value === from) this.idMap.set(key, to);
    }
  }
}

function ringWithoutClose(ring: number[][]): [number, number][] {
  if (ring.length < 2) return ring.map((c) => [Number(c[0]), Number(c[1])]);
  const first = ring[0]!;
  const last = ring[ring.length - 1]!;
  const closed =
    first[0] === last[0] && first[1] === last[1] ? ring.slice(0, -1) : ring;
  return closed.map((c) => [Number(c[0]), Number(c[1])] as [number, number]);
}

/** POST create payload из снимка фичи (включая layer_group_id). */
export function snapshotToCreatePayload(
  feature: TacticalFeature,
): Record<string, unknown> | null {
  const base = boardFeatureToCreatePayload(feature);
  if (!base) return null;
  const gid = feature.properties?.layerGroupId;
  const oid = feature.properties?.objectId;
  return {
    ...base,
    ...(gid != null && Number.isFinite(Number(gid))
      ? { layer_group_id: Number(gid) }
      : {}),
    ...(oid != null && Number.isFinite(Number(oid)) ? { object_id: Number(oid) } : {}),
  };
}

/** PATCH для полного отката стиля/геометрии/группы. */
export function snapshotToStylePatch(feature: TacticalFeature): Record<string, unknown> {
  const p = feature.properties;
  const patch: Record<string, unknown> = {
    size_px: p.sizePx ?? 32,
    rotation_deg: p.rotationDeg ?? 0,
    stroke_color: p.strokeColor ?? "#dc2626",
    stroke_width: p.strokeWidth ?? 2,
    stroke_dash: p.strokeDash ?? "solid",
    fill_color: p.fillColor ?? p.strokeColor ?? "#dc2626",
    fill_opacity: p.fillOpacity ?? 0.25,
    label: p.label ?? "",
  };
  if (p.layerGroupId == null) {
    patch.clear_layer_group = true;
  } else {
    patch.layer_group_id = p.layerGroupId;
  }
  if (p.objectId == null) {
    patch.clear_object = true;
  } else {
    patch.object_id = p.objectId;
  }
  if (p.symbolKlsId != null) {
    patch.symbol_kls_id = p.symbolKlsId;
  }
  const g = feature.geometry;
  if (g.type === "Point") {
    patch.lng = g.coordinates[0];
    patch.lat = g.coordinates[1];
  } else if (g.type === "LineString") {
    patch.coordinates = g.coordinates.map(
      (c) => [Number(c[0]), Number(c[1])] as [number, number],
    );
  } else {
    const ring = g.coordinates[0] ?? [];
    patch.coordinates = ringWithoutClose(ring);
  }
  return patch;
}

export function asFeatureSnapshot(feature: TacticalFeature | null | undefined): TacticalFeature | null {
  if (!feature?.geometry || !feature.properties) return null;
  const id = Number(feature.properties.id);
  if (!Number.isFinite(id) || id <= 0) return null;
  return {
    type: "Feature",
    geometry: feature.geometry,
    properties: { ...feature.properties, id },
  };
}
